import React from 'react';
import PropTypes from 'prop-types';
import { Chip, Box, Typography } from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import CircleIcon from '@mui/icons-material/Circle';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import InventoryIcon from '@mui/icons-material/Inventory';
import PendingIcon from '@mui/icons-material/Pending';
import WarningIcon from '@mui/icons-material/Warning';

/**
 * MuiStatusChip - Status badge/chip for order statuses
 *
 * Von Restorff Effect: Colors differentiate status at a glance
 * Jakob's Law: Familiar status patterns with color coding
 */

// Theme colors from flexflowTheme
const statusColors = {
  // Success states
  completed: { bg: '#34c38f', text: '#fff' },
  approved: { bg: '#34c38f', text: '#fff' },
  active: { bg: '#34c38f', text: '#fff' },
  delivered: { bg: '#34c38f', text: '#fff' },
  paid: { bg: '#34c38f', text: '#fff' },

  // Warning states
  pending: { bg: '#f1b44c', text: '#fff' },
  processing: { bg: '#f1b44c', text: '#fff' },
  onhold: { bg: '#f1b44c', text: '#fff' },
  partial: { bg: '#f1b44c', text: '#fff' },

  // Error states
  cancelled: { bg: '#f46a6a', text: '#fff' },
  rejected: { bg: '#f46a6a', text: '#fff' },
  failed: { bg: '#f46a6a', text: '#fff' },
  overdue: { bg: '#f46a6a', text: '#fff' },

  // Info states
  draft: { bg: '#74788d', text: '#fff' },
  new: { bg: '#556ee6', text: '#fff' },
  inprogress: { bg: '#556ee6', text: '#fff' },
  shipped: { bg: '#556ee6', text: '#fff' },

  // Default
  default: { bg: '#74788d', text: '#fff' },
};

const statusIcons = {
  completed: CheckCircleIcon,
  approved: CheckCircleIcon,
  active: CheckCircleIcon,
  delivered: CheckCircleIcon,
  paid: CheckCircleIcon,
  pending: AccessTimeIcon,
  processing: PendingIcon,
  onhold: AccessTimeIcon,
  partial: WarningIcon,
  cancelled: CancelIcon,
  rejected: CancelIcon,
  failed: CancelIcon,
  overdue: WarningIcon,
  draft: CircleIcon,
  new: CircleIcon,
  inprogress: PendingIcon,
  shipped: LocalShippingIcon,
  default: CircleIcon,
};

const StyledChip = styled(Chip)(({ theme, statuscolor, variant, size }) => {
  const colors = statusColors[statuscolor] || statusColors.default;

  const baseStyles = {
    fontWeight: 500,
    fontSize: size === 'small' ? '0.75rem' : '0.8125rem',
    height: size === 'small' ? 24 : 28,
    borderRadius: 6,
    '& .MuiChip-icon': {
      fontSize: size === 'small' ? 12 : 14,
      marginLeft: 6,
    },
    '& .MuiChip-label': {
      paddingLeft: 8,
      paddingRight: 10,
    },
  };

  if (variant === 'filled') {
    return {
      ...baseStyles,
      backgroundColor: colors.bg,
      color: colors.text,
      '&:hover': {
        backgroundColor: colors.bg,
      },
    };
  }

  if (variant === 'outlined') {
    return {
      ...baseStyles,
      backgroundColor: 'transparent',
      color: colors.bg,
      borderColor: colors.bg,
      border: `1px solid ${colors.bg}`,
    };
  }

  // Soft/light variant (default)
  return {
    ...baseStyles,
    backgroundColor: alpha(colors.bg, 0.12),
    color: colors.bg,
    '&:hover': {
      backgroundColor: alpha(colors.bg, 0.2),
    },
  };
});

// Dot indicator for minimal status display
const StatusDot = styled(Box)(({ theme, statuscolor }) => {
  const colors = statusColors[statuscolor] || statusColors.default;
  return {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: colors.bg,
    display: 'inline-block',
  };
});

const MuiStatusChip = ({
  status,
  label,
  variant = 'soft',
  size = 'medium',
  showIcon = false,
  icon,
  showDot = false,
  customColor,
  sx = {},
  ...props
}) => {
  // Normalize status string
  const normalizedStatus = status?.toLowerCase().replace(/[\s-_]/g, '') || 'default';

  // Determine display label
  const displayLabel = label || status || 'Unknown';

  // Get status color key
  const statusColor = customColor ? 'custom' : normalizedStatus;

  // Get icon component
  const IconComponent = icon || statusIcons[normalizedStatus] || statusIcons.default;

  // For dot-only display
  if (showDot && !label && !status) {
    return <StatusDot statuscolor={statusColor} sx={sx} />;
  }

  // With dot indicator
  if (showDot) {
    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, ...sx }}>
        <StatusDot statuscolor={statusColor} />
        <Typography variant="body2" component="span">
          {displayLabel}
        </Typography>
      </Box>
    );
  }

  return (
    <StyledChip
      label={displayLabel}
      statuscolor={statusColor}
      variant={variant}
      size={size}
      icon={showIcon ? <IconComponent /> : undefined}
      sx={{
        ...(customColor && {
          backgroundColor: variant === 'filled'
            ? customColor
            : alpha(customColor, 0.12),
          color: variant === 'filled' ? '#fff' : customColor,
          ...(variant === 'outlined' && {
            backgroundColor: 'transparent',
            borderColor: customColor,
          }),
        }),
        ...sx,
      }}
      {...props}
    />
  );
};

// Preset configurations for common statuses
MuiStatusChip.Order = ({ status, ...props }) => {
  const orderStatusMap = {
    new: 'new',
    pending: 'pending',
    processing: 'processing',
    shipped: 'shipped',
    delivered: 'delivered',
    completed: 'completed',
    cancelled: 'cancelled',
  };
  return <MuiStatusChip status={orderStatusMap[status] || status} {...props} />;
};

MuiStatusChip.Payment = ({ status, ...props }) => {
  const paymentStatusMap = {
    paid: 'paid',
    pending: 'pending',
    partial: 'partial',
    overdue: 'overdue',
    failed: 'failed',
  };
  return <MuiStatusChip status={paymentStatusMap[status] || status} {...props} />;
};

MuiStatusChip.Inventory = ({ status, ...props }) => {
  const inventoryStatusMap = {
    instock: 'active',
    lowstock: 'partial',
    outofstock: 'cancelled',
  };
  return <MuiStatusChip status={inventoryStatusMap[status] || status} {...props} />;
};

MuiStatusChip.propTypes = {
  /** Status value (determines color automatically) */
  status: PropTypes.string,
  /** Custom label text */
  label: PropTypes.string,
  /** Visual variant */
  variant: PropTypes.oneOf(['filled', 'outlined', 'soft']),
  /** Chip size */
  size: PropTypes.oneOf(['small', 'medium']),
  /** Show status icon */
  showIcon: PropTypes.bool,
  /** Custom icon component */
  icon: PropTypes.elementType,
  /** Show as dot with label */
  showDot: PropTypes.bool,
  /** Custom color override */
  customColor: PropTypes.string,
  /** Custom styles */
  sx: PropTypes.object,
};

export default MuiStatusChip;
